import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

interface AvarosFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const defaultService = () => ({
  appointment_type: '',
  appointment_mode: []
});

const defaultProvider = () => ({
  provider_name: '',
  accept_new_patient: false,
  appointment_mode: [],
  appointment_types: [],
  services: [defaultService()]
});

const AvarosForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: AvarosFormProps) => {
  const providersField = configFields?.find((f: any) => f.key === 'providers');
  const otherFields = configFields?.filter((f: any) => f.key !== 'providers');

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);

    if (!existing || existing.length === 0) {
      append(defaultProvider());
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box>
        <Typography fontSize={16} fontWeight={600} my={2}>
          Provider Details
        </Typography>

        {providers.map((provider: any, index: number) => (
          <ProviderItem
            key={provider.id}
            index={index}
            control={control}
            remove={remove}
            disabled={disabled}
            getValues={getValues}
            setValue={setValue}
            namePrefix={namePrefix}
            providersFields={providersField}
            totalProviders={providers.length}
          />
        ))}

        <Box display='flex' justifyContent='center'>
          <Button variant='contained' disabled={disabled} onClick={() => append(defaultProvider())}>
            + Add Another Provider
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AvarosForm;

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  getValues,
  setValue,
  namePrefix,
  totalProviders,
  providersFields
}: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;
  const providerField = providersFields?.group?.find((f: any) => f.key === 'provider_name');
  const appointmentTypesField = providersFields?.group?.find((f: any) => f.key === 'appointment_types');
  const appointmentModeField = providersFields?.group?.find((f: any) => f.key === 'appointment_mode');
  const providerOptions = providerField?.options || [];
  const appointmentTypeOptions = appointmentTypesField?.options || [];
  const appointmentModeOptions = appointmentModeField?.options || [];

  const {
    fields: appointmentTypes,
    append: addAppointmentType,
    remove: removeAppointmentType
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });

  const services = useWatch({
    control,
    name: `${providerPrefix}.services`
  });

  useEffect(() => {
    const provider = getValues(providerPrefix);
    const legacyTypes = Array.isArray(provider?.appointment_types) ? provider.appointment_types : [];
    const hasServices = Array.isArray(provider?.services) && provider.services.length > 0;

    if (!hasServices && legacyTypes.length > 0) {
      setValue(
        `${providerPrefix}.services`,
        legacyTypes.map((type: string) => ({
          appointment_type: type,
          appointment_mode: Array.isArray(provider.appointment_mode)
            ? provider.appointment_mode
            : provider.appointment_mode
              ? [provider.appointment_mode]
              : []
        }))
      );
    }
  }, [getValues, providerPrefix, setValue]);

  useEffect(() => {
    if (appointmentTypes.length === 0) {
      addAppointmentType(defaultService());
    }
  }, [addAppointmentType, appointmentTypes.length, getValues, providerPrefix]);

  useEffect(() => {
    if (!Array.isArray(services)) return;

    const types = services.map((service: any) => service?.appointment_type).filter(Boolean);
    setValue(`${providerPrefix}.appointment_types`, types);
  }, [providerPrefix, services, setValue]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>

        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={5} alignItems='center'>
        <Grid size={9}>
          <Select
            label='Select Provider'
            name={`${providerPrefix}.provider_name`}
            control={control}
            disabled={disabled}
            options={providerOptions}
            fullWidth
          />
        </Grid>

        <Grid size={3}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accept New Patients'
              />
            )}
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={1}>
          <Typography fontWeight={500}>Appointment Types</Typography>

          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() => {
              addAppointmentType(defaultService());
            }}
          >
            Add Appointment Types
          </Button>
        </Box>

        {appointmentTypes.map((appointmentType: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;

          return (
            <Box key={appointmentType.id} mb={2}>
              <Box display='flex' gap={2} alignItems='center'>
                <Select
                  label={appointmentTypesField?.label || 'Select Type'}
                  name={`${servicePrefix}.appointment_type`}
                  control={control}
                  disabled={disabled}
                  options={appointmentTypeOptions}
                  fullWidth
                />

                <Select
                  label={appointmentModeField?.label || 'Select Mode'}
                  name={`${servicePrefix}.appointment_mode`}
                  control={control}
                  disabled={disabled}
                  options={appointmentModeOptions}
                  multiple
                  fullWidth
                />

                {appointmentTypes.length > 1 && !disabled && (
                  <IconButton onClick={() => removeAppointmentType(sIndex)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
